import { createContext, useContext, useState, useEffect, ReactNode, createElement } from 'react';
import {
  getRefreshToken, getStoredUser, setTokens, setStoredUser,
  clearTokens, hasRole, type StoredUser,
} from '../lib/auth';
import type { AuthDto, Role } from '../types/api';
import client from '../api/client';

interface AuthContextValue {
  currentUser: StoredUser | null;
  isLoading: boolean;
  login: (dto: AuthDto) => void;
  logout: () => void;
  can: (minRole: Role) => boolean;
  hasPermission: (code: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [currentUser, setCurrentUser] = useState<StoredUser | null>(null);
  const [isLoading, setIsLoading]     = useState(true);

  useEffect(() => {
    const refresh = getRefreshToken();

    if (!refresh || !getStoredUser()) { setIsLoading(false); return; }

    client
      .post<AuthDto>('/auth/refresh', { refreshToken: refresh })
      .then((r) => {
        const user: StoredUser = {
          id: r.data.user.id,
          name: r.data.user.name,
          email: r.data.user.email,
          role: r.data.user.role,
          permissions: r.data.user.permissions ?? [],
        };
        setTokens(r.data.accessToken, r.data.refreshToken);
        setStoredUser(user);
        setCurrentUser(user);
      })
      .catch(() => clearTokens())
      .finally(() => setIsLoading(false));
  }, []);

  const login = (dto: AuthDto) => {
    const user: StoredUser = {
      id: dto.user.id,
      name: dto.user.name,
      email: dto.user.email,
      role: dto.user.role,
      permissions: dto.user.permissions ?? [],
    };
    setTokens(dto.accessToken, dto.refreshToken);
    setStoredUser(user);
    setCurrentUser(user);
  };

  const logout = () => {
    const refresh = getRefreshToken();
    clearTokens();
    setCurrentUser(null);
    if (refresh) client.post('/auth/logout', { refreshToken: refresh }).catch(() => {});
  };

  const can = (minRole: Role) => !!currentUser && hasRole(currentUser.role, minRole);
  const hasPermission = (code: string) => !!currentUser?.permissions.includes(code);

  return createElement(AuthContext.Provider, { value: { currentUser, isLoading, login, logout, can, hasPermission } }, children);
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

// Canonical permission codes — use these instead of string literals in components.
export const Permissions = {
  DashboardView:    'dashboard:view',
  TasksView:        'tasks:view',
  TasksManage:      'tasks:manage',
  ProjectsView:     'projects:view',
  ProjectsManage:   'projects:manage',
  CheckInsSubmit:   'checkins:submit',
  CheckInsView:     'checkins:view',
  EscalationsView:  'escalations:view',
  EngineersView:    'engineers:view',
  OverworkView:     'overwork:view',
  OverworkOverride: 'overwork:override',
  ReportsView:      'reports:view',
  FeedbackSubmit:   'feedback:submit',
  FeedbackView:     'feedback:view',
  PulseSubmit:      'pulse:submit',
  PulseView:        'pulse:view',
  NotificationsView:'notifications:view',
  ThresholdsManage: 'thresholds:manage',
  UsersManage:      'users:manage',
  AuditView:        'audit:view',
} as const;
